// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Skeleton } from '@material-ui/lab';

// Internal Dependencies
import MText from '../../../../components/Common/MText';
import ApiService from '../../../../services/apiService';

const overviewBackgroundColor = '#E4E4E4';

const overviewItems = [
  'Cards Live',
  'Receipt Uploads',
  'Receipt Rejects',
  'Stamps',
  'Vouchers Issued',
  'Vouchers Claimed',
];

const Overview = (props) => {
  const [overviewData, setOverviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOverviewData = async (pageId, loyaltyCardId) => {
    try {
      setIsLoading(true);
      const response = await ApiService.getLoyaltyCardOverview(pageId, loyaltyCardId);
      setOverviewData(response);
    } catch (error) {
      toast.error('Error while fetching overview data', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData(props.pageId, props.loyaltyCardId);
  }, [props.pageId, props.loyaltyCardId]);

  return (
    <div
      className="px-2 px-xl-5 pb-2 pb-xl-4"
      style={{
        backgroundColor: overviewBackgroundColor,
        borderRadius:    '4px',
        paddingTop:      '24px',
      }}
    >
      <MText
        bold
        className="text-center"
        size="1rem"
        text={`OVERVIEW (YTD ${moment().utcOffset('+08:00').format('DD MMM YYYY')})`}
      />
      <div
        className="overflow-auto"
      >
        <div
          style={{ minWidth: 954 }}
        >
          <div
            className="bg-white mt-2"
            style={{ borderRadius: '4px' }}
          >
            <div className="row mx-0 pt-3 pb-1 flex-nowrap">
              {
                overviewItems.map((item) => (
                  <div className="col-2" key={item}>
                    <MText text={item} size="0.9rem" className="text-center" />
                  </div>
                ))
              }
            </div>

            <div className="row mx-0 pt-1 pb-3 flex-nowrap">
              <div className="col-2">
                {
                  isLoading ? (
                    <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                  ) : (
                    <MText text={overviewData?.overview.cardsLive ?? '-'} size=".9rem" className="text-center" />
                  )
                }
              </div>
              <div className="col-2">
                {
                  isLoading ? (
                    <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                  ) : (
                    <MText text={overviewData?.overview.receiptUploads ?? '-'} size=".9rem" className="text-center" />
                  )
                }
              </div>
              <div className="col-2">
                {
                  isLoading ? (
                    <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                  ) : (
                    <MText text={overviewData?.overview.receiptRejects ?? '-'} size=".9rem" className="text-center" />
                  )
                }
              </div>
              <div className="col-2">
                {
                  isLoading ? (
                    <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                  ) : (
                    <MText text={overviewData?.overview.stamps ?? '-'} size=".9rem" className="text-center" />
                  )
                }
              </div>
              <div className="col-2">
                {
                  isLoading ? (
                    <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                  ) : (
                    <MText text={overviewData?.overview.vouchersIssued ?? '-'} size=".9rem" className="text-center" />
                  )
                }
              </div>
              <div className="col-2">
                {
                  isLoading ? (
                    <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                  ) : (
                    <MText text={overviewData?.overview.vouchersClaimed ?? '-'} size=".9rem" className="text-center" />
                  )
                }
              </div>
            </div>
          </div>

          <div
            className="mt-4"
            style={{ borderRadius: '4px' }}
          >
            <table
              className="table table-bordered bg-white overflow-hidden"
              style={{
                borderRadius:   12,
                borderCollapse: 'separate',
                borderSpacing:  0,
              }}
            >
              <thead>
                <tr>
                  <th scope="col" style={{ width: 240 }}>No. of Stamps</th>
                  {
                      new Array(10).fill(0).map((_, idx) => (
                        <th scope="col" className="text-center" style={{ width: 60 }}>{idx + 1}</th>
                      ))
                  }
                  <th scope="col" className="text-center" style={{ width: 'auto' }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="font-weight-bold">Cards (Live)</td>
                  {
                      new Array(10).fill(0).map((_, idx) => (
                        <td className="text-center">
                          {
                            isLoading ? (
                              <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                            ) : (
                              overviewData?.loyaltyCardStampCounts.find((d) => d.stampCount === idx + 1)?.totalCards ?? '-'
                            )
                          }
                        </td>
                      ))
                  }
                  <td className="text-center">
                    {
                      isLoading ? (
                        <Skeleton variant="text" className="mx-auto" sx={{ fontSize: '1rem' }} width={20} />
                      ) : (
                        overviewData?.loyaltyCardStampCounts.reduce((acc, cum) => acc + cum.totalCards, 0) ?? '-'
                      )
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

Overview.propTypes = {
  pageId:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  loyaltyCardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default Overview;
